// Shared constants + helpers for the Incentives Operating System UI.

export const C = {
  bg: '#FAF8F5',
  card: '#FFFFFF',
  muted: '#F3EFEA',
  border: '#E8E2D9',
  text: '#1A1A1E',
  text2: '#52525B',
  text3: '#8C8C9A',
  burgundy: '#8B1E3F',
  burgundyHover: '#721733',
  rose: '#E63946',
  gold: '#D4AF37',
  teal: '#0D9488',
  indigo: '#6366F1',
};

export const fontSerif = "'Cormorant Garamond', 'Playfair Display', Georgia, serif";
export const fontMono = "'JetBrains Mono', 'Space Mono', monospace";

// Reward / band visual mapping (mirrors design_guidelines.json)
export const BAND_STYLES = {
  UNLOCKED: {
    label: 'Reward Unlocked',
    badge: 'bg-amber-100 text-amber-900 border-amber-300',
    ring: '#D4AF37',
    icon: 'Trophy',
  },
  ALMOST_THERE: {
    label: 'Almost There',
    badge: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    ring: '#10B981',
    icon: 'Sparkles',
  },
  BUILDING: {
    label: 'Building Momentum',
    badge: 'bg-blue-100 text-blue-900 border-blue-300',
    ring: '#3B82F6',
    icon: 'TrendingUp',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    badge: 'bg-slate-100 text-slate-800 border-slate-300',
    ring: '#64748B',
    icon: 'Target',
  },
  LOCKED: {
    label: 'In Progress',
    badge: 'bg-slate-100 text-slate-800 border-slate-300',
    ring: '#64748B',
    icon: 'Target',
  },
  DISQUALIFIED: {
    label: 'Gate Blocked',
    badge: 'bg-rose-100 text-rose-900 border-rose-300',
    ring: '#E63946',
    icon: 'AlertTriangle',
  },
};

export function bandFor(scoreBand, rewardStatus) {
  if (rewardStatus === 'DISQUALIFIED') return BAND_STYLES.DISQUALIFIED;
  const key = scoreBand?.key || 'IN_PROGRESS';
  return BAND_STYLES[key] || BAND_STYLES.IN_PROGRESS;
}

// KPI type → measurement theme (Monthly Output vs Daily Adherence vs Quality)
export function kpiTheme(type) {
  if (type === 'OUTPUT_COUNT') {
    return { key: 'output', label: 'Monthly Output', color: C.teal, tint: '#CCFBF1',
      badge: 'bg-teal-50 text-teal-800 border-teal-200' };
  }
  if (type === 'DAILY_ADHERENCE' || type === 'TEAM_DAILY_ADHERENCE') {
    return { key: 'adherence', label: 'Daily Adherence', color: C.indigo, tint: '#E0E7FF',
      badge: 'bg-indigo-50 text-indigo-800 border-indigo-200' };
  }
  if (type === 'MANUAL_ACCEPTED_COUNT') {
    return { key: 'manual', label: 'Monthly', color: '#B45309', tint: '#FEF3C7',
      badge: 'bg-amber-50 text-amber-800 border-amber-200' };
  }
  return { key: 'quality', label: 'Quality', color: C.burgundy, tint: '#FDF0F2',
    badge: 'bg-rose-50 text-rose-900 border-rose-200' };
}

export function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// Python-style weekday: Monday=0 ... Sunday=6
export function pyWeekday(dateStr) {
  const d = new Date(dateStr + 'T00:00:00Z');
  return (d.getUTCDay() + 6) % 7;
}

export function isWorkingDay(dateStr, workingWeekdays, holidays) {
  const ww = workingWeekdays && workingWeekdays.length ? workingWeekdays : [0, 1, 2, 3, 4];
  return ww.includes(pyWeekday(dateStr)) && !(holidays || []).includes(dateStr);
}

// Count working days in [start, end] inclusive
export function workingDaysBetween(startStr, endStr, workingWeekdays, holidays) {
  let count = 0;
  let cur = startStr;
  let guard = 0;
  while (cur <= endStr && guard < 400) {
    if (isWorkingDay(cur, workingWeekdays, holidays)) count += 1;
    cur = addDays(cur, 1);
    guard += 1;
  }
  return count;
}

export function fmtNum(n, digits = 0) {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return v.toLocaleString('en-IN', { maximumFractionDigits: digits });
}

export function fmtDateShort(dateStr) {
  try {
    const d = new Date(dateStr + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch {
    return dateStr;
  }
}

export function currencySymbol(code) {
  const map = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
  return map[code] || '';
}
